import { toast } from '@/components/toast';
import { createKnowledgeDocument } from '@/services/api';
import { logger } from '@/services/logger';
import { UploadingFile } from '@/state/agent';
import { IKnowledgeDocument } from '@d-id/types';
import { AxiosError } from 'axios';
import { MutationFunction, useMutation } from 'react-query';
import { useCreateKnowledge } from './useCreateKnowledge';

const RETRIES = 3;

export function useCreateKnowledgeAndDocuments() {
    const { mutateAsync: createKnowledge } = useCreateKnowledge();

    const createKnowledgeAndDocumentsMut: MutationFunction<
        { id: string },
        { agentName: string; uploadingFiles: UploadingFile[] }
    > = async ({ agentName, uploadingFiles }) => {
        const knowledgeResponse = await createKnowledge({ agentName });

        if (!!uploadingFiles && uploadingFiles.length > 0) {
            const uploadDocumentPromises = uploadingFiles.map(async (uploadingFile: UploadingFile) => {
                const knowledgeDocument: IKnowledgeDocument = {
                    documentType: uploadingFile.file.type,
                    title: uploadingFile.file.name,
                    source_url: uploadingFile.s3Url as string,
                };
                await createKnowledgeDocument(knowledgeResponse.id, knowledgeDocument);
            });

            await Promise.all(uploadDocumentPromises);
        }
        return knowledgeResponse;
    };

    return useMutation('create-knowledge-and-documents', createKnowledgeAndDocumentsMut, {
        retry(failureCount, error: AxiosError) {
            return (
                failureCount < RETRIES &&
                !!error.response?.status &&
                ([401, 403].some(status => error.response!.status === status) || error.response.status >= 500)
            );
        },
        onError: async (error: any) => {
            logger.error('Could not create knowledge', { error });
            toast.error({ title: `Could not create knowledge`, toastId: `knowledge-create-error` });
        },
    });
}
